/**
 * BomCard — Bill of Materials for a product (BLU-534).
 *
 * Lists the trim/component SKUs linked to this product, with quantity-per-unit
 * and unit of measure. Linked trims are deducted from inventory automatically
 * as orders for this product are fulfilled. Includes an add row (trim picker +
 * qty + UOM) and a per-row "link to more products" bulk action so one trim is
 * easy to attach across the catalog.
 */
import { useMemo, useState } from 'react';
import { Layers, Link2, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const UOM_OPTIONS = ['pcs', 'm', 'cm', 'g', 'kg'] as const;
type Uom = (typeof UOM_OPTIONS)[number];

// BLU-534 gap-fill: WHEN this trim is consumed. 'order' (default) = fulfilment
// time (POD — attached when we print/pack). 'receipt' = PO-receipt time
// (cut & sew — the factory already consumed it before goods arrived).
const DEDUCT_AT_OPTIONS = [
  { value: 'order', label: 'Order fulfilment' },
  { value: 'receipt', label: 'PO receipt' },
] as const;
type DeductAt = (typeof DEDUCT_AT_OPTIONS)[number]['value'];

interface Props {
  productId: string;
}

export function BomCard({ productId }: Props) {
  const { can } = useRole();
  const canWrite = can('inventory.write');
  const utils = trpc.useUtils();

  const componentsQuery = trpc.skuComponents.list.useQuery(
    { parentProductId: productId },
    { staleTime: 30_000 },
  );
  const trimsQuery = trpc.skuComponents.listTrims.useQuery(undefined, {
    staleTime: 60_000,
    enabled: canWrite,
  });

  const [newTrimId, setNewTrimId] = useState('');
  const [newQty, setNewQty] = useState('1');
  const [newUom, setNewUom] = useState<Uom>('pcs');
  const [newDeductAt, setNewDeductAt] = useState<DeductAt>('order');
  const [bulkLinkFor, setBulkLinkFor] = useState<{ skuId: string; label: string } | null>(null);

  const invalidate = () => utils.skuComponents.list.invalidate();

  const upsertMut = trpc.skuComponents.upsert.useMutation({
    onSuccess: () => {
      invalidate();
      setNewTrimId('');
      setNewQty('1');
      setNewUom('pcs');
      setNewDeductAt('order');
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteMut = trpc.skuComponents.delete.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const components = useMemo(() => componentsQuery.data ?? [], [componentsQuery.data]);
  const linkedTrimIds = useMemo(
    () => new Set(components.map((c) => c.componentSkuId)),
    [components],
  );
  const availableTrims = useMemo(
    () => (trimsQuery.data ?? []).filter((t) => !linkedTrimIds.has(t.id)),
    [trimsQuery.data, linkedTrimIds],
  );

  const addComponent = () => {
    const qty = Number(newQty);
    if (!newTrimId || !Number.isFinite(qty) || qty <= 0) return;
    upsertMut.mutate({
      parentProductId: productId,
      componentSkuId: newTrimId,
      quantityPerUnit: qty,
      unitOfMeasure: newUom,
      deductAt: newDeductAt,
    });
  };

  const saveQty = (
    componentId: string,
    componentSkuId: string,
    qty: number,
    uom: string,
    deductAt: DeductAt,
  ) => {
    if (!Number.isFinite(qty) || qty <= 0) return;
    upsertMut.mutate({
      id: componentId,
      parentProductId: productId,
      componentSkuId,
      quantityPerUnit: qty,
      unitOfMeasure: uom as Uom,
      deductAt,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
            <Layers className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-base">Bill of Materials</CardTitle>
            <CardDescription>
              Trims deducted from stock automatically as orders for this product are fulfilled.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {componentsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : components.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No trims linked yet{canWrite ? '. Add one below.' : '.'}
          </p>
        ) : (
          <div className="space-y-1">
            {components.map((c) => (
              <ComponentRow
                key={c.id}
                component={c}
                canWrite={canWrite}
                saving={upsertMut.isPending}
                onSave={(qty, uom, deductAt) => saveQty(c.id, c.componentSkuId, qty, uom, deductAt)}
                onDelete={() => deleteMut.mutate({ id: c.id })}
                onBulkLink={() =>
                  setBulkLinkFor({
                    skuId: c.componentSkuId,
                    label: c.component?.sku ?? 'trim',
                  })
                }
              />
            ))}
          </div>
        )}

        {canWrite && (
          <div className="space-y-1.5 border-t pt-4">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-48 flex-1 space-y-1">
                <span className="text-xs font-medium">Trim</span>
                <Select value={newTrimId} onValueChange={setNewTrimId}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue
                      placeholder={
                        trimsQuery.isLoading
                          ? 'Loading trims…'
                          : availableTrims.length === 0
                            ? 'No trim SKUs yet: create one via Inventory → Add SKU'
                            : 'Pick a trim SKU'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTrims.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="font-mono">{t.sku}</span>
                        {t.title ? ` · ${t.title}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24 space-y-1">
                <span className="text-xs font-medium">Qty / unit</span>
                <Input
                  type="number"
                  min="0.0001"
                  step="any"
                  value={newQty}
                  onChange={(e) => setNewQty(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <div className="w-20 space-y-1">
                <span className="text-xs font-medium">UOM</span>
                <Select value={newUom} onValueChange={(v) => setNewUom(v as Uom)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UOM_OPTIONS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36 space-y-1">
                <span className="text-xs font-medium">Deducts at</span>
                <Select value={newDeductAt} onValueChange={(v) => setNewDeductAt(v as DeductAt)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEDUCT_AT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                size="sm"
                className="h-8"
                onClick={addComponent}
                disabled={!newTrimId || upsertMut.isPending}
              >
                {upsertMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                <span className="ml-1.5">Add</span>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Receipt = the factory consumed it before goods arrived.
            </p>
          </div>
        )}
      </CardContent>

      {bulkLinkFor && (
        <BulkLinkDialog
          componentSkuId={bulkLinkFor.skuId}
          componentLabel={bulkLinkFor.label}
          currentProductId={productId}
          onClose={() => setBulkLinkFor(null)}
        />
      )}
    </Card>
  );
}

type ComponentItem = {
  id: string;
  componentSkuId: string;
  quantityPerUnit: number;
  unitOfMeasure: string;
  deductAt: DeductAt;
  component: { id: string; sku: string; title: string | null } | null;
};

function ComponentRow({
  component: c,
  canWrite,
  saving,
  onSave,
  onDelete,
  onBulkLink,
}: {
  component: ComponentItem;
  canWrite: boolean;
  saving: boolean;
  onSave: (qty: number, uom: string, deductAt: DeductAt) => void;
  onDelete: () => void;
  onBulkLink: () => void;
}) {
  const [qty, setQty] = useState(String(c.quantityPerUnit));
  const [uom, setUom] = useState(c.unitOfMeasure);
  const [deductAt, setDeductAt] = useState<DeductAt>(c.deductAt);

  const dirty =
    Number(qty) !== c.quantityPerUnit || uom !== c.unitOfMeasure || deductAt !== c.deductAt;

  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 -mx-2 hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <span className="font-mono text-xs font-medium">{c.component?.sku}</span>
        {c.component?.title && (
          <span className="ml-2 truncate text-xs text-muted-foreground">{c.component.title}</span>
        )}
      </div>
      {canWrite ? (
        <>
          <Input
            type="number"
            min="0.0001"
            step="any"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="h-7 w-20 text-xs"
            aria-label={`Quantity of ${c.component?.sku} per unit`}
          />
          <Select value={uom} onValueChange={setUom}>
            <SelectTrigger className="h-7 w-18 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UOM_OPTIONS.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={deductAt} onValueChange={(v) => setDeductAt(v as DeductAt)}>
            <SelectTrigger
              className="h-7 w-32 text-xs"
              title="Receipt = the factory consumed it before goods arrived."
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEDUCT_AT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dirty && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              disabled={saving}
              onClick={() => onSave(Number(qty), uom, deductAt)}
            >
              Save
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={onBulkLink}
            title="Link this trim to more products"
          >
            <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            title="Remove from BOM"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">
          {c.quantityPerUnit} {c.unitOfMeasure} ·{' '}
          {DEDUCT_AT_OPTIONS.find((o) => o.value === c.deductAt)?.label ?? c.deductAt}
        </span>
      )}
    </div>
  );
}

function BulkLinkDialog({
  componentSkuId,
  componentLabel,
  currentProductId,
  onClose,
}: {
  componentSkuId: string;
  componentLabel: string;
  currentProductId: string;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const skusQuery = trpc.inventory.listSkus.useQuery({}, { staleTime: 60_000 });
  const linkedQuery = trpc.skuComponents.list.useQuery(
    { componentSkuId },
    { staleTime: 10_000 },
  );
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState('1');
  const [uom, setUom] = useState<Uom>('pcs');

  const linkedProductIds = useMemo(
    () =>
      new Set(
        (linkedQuery.data ?? [])
          .map((c) => c.parentProductId)
          .filter((id): id is string => !!id),
      ),
    [linkedQuery.data],
  );

  const products = useMemo(() => {
    const raw = (skusQuery.data ?? []) as Array<{
      productId: string | null;
      title: string | null;
      isTrim?: boolean;
    }>;
    const byId = new Map<string, { id: string; title: string }>();
    for (const s of raw) {
      if (!s.productId || s.isTrim) continue;
      if (s.productId === currentProductId || linkedProductIds.has(s.productId)) continue;
      if (!byId.has(s.productId)) {
        byId.set(s.productId, { id: s.productId, title: s.title ?? 'Untitled product' });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [skusQuery.data, currentProductId, linkedProductIds]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? products.filter((p) => p.title.toLowerCase().includes(term)) : products;
  }, [products, search]);

  const bulkLinkMut = trpc.skuComponents.bulkLink.useMutation({
    onSuccess: (r) => {
      toast.success(`Linked to ${r.linked} product${r.linked === 1 ? '' : 's'}`);
      utils.skuComponents.list.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Link <span className="font-mono">{componentLabel}</span> to more products
          </DialogTitle>
          <DialogDescription>
            Each selected product gets this trim added to its bill of materials. Products that
            already have it are hidden.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="max-h-60 overflow-auto rounded-md border p-1">
            {skusQuery.isLoading || linkedQuery.isLoading ? (
              <p className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </p>
            ) : visible.length === 0 ? (
              <p className="px-2 py-1 text-xs text-muted-foreground">No matches.</p>
            ) : (
              visible.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted/40"
                >
                  <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="truncate text-xs">{p.title}</span>
                </label>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-24 space-y-1">
              <span className="text-xs font-medium">Qty / unit</span>
              <Input
                type="number"
                min="0.0001"
                step="any"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="w-20 space-y-1">
              <span className="text-xs font-medium">UOM</span>
              <Select value={uom} onValueChange={(v) => setUom(v as Uom)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UOM_OPTIONS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selected.size === 0 || bulkLinkMut.isPending || Number(qty) <= 0}
            onClick={() =>
              bulkLinkMut.mutate({
                componentSkuId,
                parentProductIds: Array.from(selected),
                quantityPerUnit: Number(qty),
                unitOfMeasure: uom,
              })
            }
          >
            {bulkLinkMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Link {selected.size > 0 ? `${selected.size} ` : ''}product
            {selected.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
